/**
 * Solo Gamemaster Store
 *
 * Client-side orchestration for solo play mode.
 * Routes player messages to the AI DM via tRPC,
 * dispatches commands via CommandBridge, manages streaming state.
 */

import { create } from 'zustand';
import { trpc } from '../lib/trpc';
import { useChatStore, type ChatMessage } from './chatStore';
import { useCharacterStore } from './characterStore';
import { useTokenStore } from './tokenStore';
import { executeCommands, getPartyPositions } from '../agents/command-bridge';
import { getDefaultBadge } from '../components/badges';
import type { GamemasterCommand, PendingRollRequest, PresentChoicesCommand, Character } from '@machina/core';
import { generateMissingTokens } from '../lib/assetPaths';
import { useMapStore } from './mapStore';

const CREATE_CHARACTER_BUTTONS = [{ label: 'Create Character', action: 'create_character' }];

/**
 * Ensure all party characters have tokens on the board.
 * Auto-generates token images via FLUX for characters without one.
 */
function ensurePartyTokens(characters: Character[]): void {
  const tokenStore = useTokenStore.getState();
  const existingOwners = new Set(
    Array.from(tokenStore.tokens.values()).map((t) => t.ownerId).filter(Boolean),
  );

  // Get map dimensions for south-edge placement
  const mapBounds = useMapStore.getState().getMapBoundsInPixels();
  const gridSize = tokenStore.gridSize; // 50px = 5ft
  const mapW = mapBounds?.width ?? 1000;
  const mapH = mapBounds?.height ?? 1000;

  const charsToAdd = characters.filter(c => !existingOwners.has(c.id));
  const positions = getPartyPositions(charsToAdd.length, mapW, mapH, gridSize);

  const newNames: string[] = [];
  for (let idx = 0; idx < charsToAdd.length; idx++) {
    const char = charsToAdd[idx];
    const badge = getDefaultBadge(idx);
    tokenStore.addToken({
      name: char.name,
      position: positions[idx] ?? { x: 200 + idx * 80, y: mapH - gridSize * 2 },
      combatantId: char.id,
      ownerId: char.id,
      currentHp: char.currentHp,
      maxHp: char.maxHp,
      ac: char.armorClass ?? 10,
      visuals: {
        shape: 'circle',
        size: 'medium',
        color: badge.accent,
        showHpBar: true,
        showConditionIcons: true,
        label: char.name.slice(0, 2).toUpperCase(),
        // Use existing avatar if available — skip FLUX generation
        imageUrl: char.avatarUrl || undefined,
      },
    });
    // Only need FLUX generation if no avatarUrl
    if (!char.avatarUrl) {
      newNames.push(char.name);
    }
    console.log('[SoloGamemaster] Created PC token for', char.name, char.avatarUrl ? '(avatar)' : '(needs generation)');
  }

  // Auto-generate token images for characters without avatarUrl
  if (newNames.length > 0) {
    generateMissingTokens(newNames, (name, imageUrl) => {
      // Match by ownerId instead of name to avoid case mismatch
      const tokens = useTokenStore.getState().tokens;
      const char = characters.find(c => c.name.toLowerCase() === name.toLowerCase());
      if (!char) return;
      for (const [, token] of tokens) {
        if (token.ownerId === char.id) {
          useTokenStore.getState().updateToken({
            id: token.id,
            visuals: { imageUrl },
          });
          break;
        }
      }
    });
  }
}

interface SoloGamemasterState {
  // Is this campaign running in solo mode?
  isSoloMode: boolean;
  campaignId: string | null;

  // AI DM state
  isProcessing: boolean;
  isInitialized: boolean;
  pendingRoll: PendingRollRequest | null;
  error: string | null;

  // Actions
  setSoloMode: (campaignId: string | null) => void;
  initialize: () => Promise<void>;
  handlePlayerInput: (text: string) => Promise<void>;
  handleRollResult: (notation: string, total: number, natural: number, label: string) => Promise<void>;
  endCombatTurn: () => Promise<void>;
  clearPendingRoll: () => void;
}

export const useSoloGamemasterStore = create<SoloGamemasterState>((set, get) => ({
  isSoloMode: false,
  campaignId: null,
  isProcessing: false,
  isInitialized: false,
  pendingRoll: null,
  error: null,

  setSoloMode: (campaignId) => {
    set({
      isSoloMode: !!campaignId,
      campaignId,
      isInitialized: false,
      pendingRoll: null,
      error: null,
    });
  },

  initialize: async () => {
    const { campaignId, isSoloMode } = get();
    if (!campaignId || !isSoloMode) return;

    set({ isProcessing: true, error: null });

    // Check if player has a character in this campaign (client-side)
    const chars = useCharacterStore.getState().characters;
    const hasCharacter = Array.from(chars.values()).some(
      (c) => c.campaignId === campaignId && !c.isNpc,
    );

    // No character? Show welcome + Create Character button directly (no API call needed)
    if (!hasCharacter) {
      useChatStore.getState().addMessage({
        sender: 'Dungeon Master',
        message: 'Welcome, adventurer! Before we begin, you\'ll need to create a character.',
        type: 'ai_dm',
        choiceButtons: CREATE_CHARACTER_BUTTONS,
      });
      set({ isProcessing: false });
      return;
    }

    try {
      const result = await trpc.solo.initialize.mutate({ campaignId });
      const commands = (result.commands || []) as GamemasterCommand[];

      // Check for choice buttons to embed in the narration message
      const choiceCmd = commands.find((c): c is PresentChoicesCommand => c.type === 'present_choices');

      // Add AI DM narration to chat (with choice buttons if present)
      if (result.narration || choiceCmd) {
        useChatStore.getState().addMessage({
          sender: 'Dungeon Master',
          message: result.narration || '',
          type: 'ai_dm',
          choiceButtons: choiceCmd?.choices,
        });
      }

      // Execute non-choice commands (map generation, scene setup, etc.)
      const otherCommands = commands.filter((c) => c.type !== 'present_choices');
      if (otherCommands.length > 0) {
        await executeCommands(otherCommands, campaignId);
      }

      // Ensure all PCs have tokens on the board (with auto-generated images)
      const allChars = useCharacterStore.getState().characters;
      const pcChars = Array.from(allChars.values()).filter(
        (c) => c.campaignId === campaignId && !c.isNpc,
      );
      if (pcChars.length > 0) ensurePartyTokens(pcChars);

      // Track pending roll if any
      const pendingRoll = result.pendingRoll?.pendingRoll || null;
      if (pendingRoll) {
        addRollRequestToChat(pendingRoll);
      }

      set({ isInitialized: true, isProcessing: false, pendingRoll });
    } catch (err) {
      console.error('[SoloGamemaster] Initialize failed:', err);
      set({ error: (err as Error).message, isProcessing: false });
    }
  },

  handlePlayerInput: async (text) => {
    const { campaignId, isSoloMode, isProcessing } = get();
    if (!campaignId || !isSoloMode) return;
    if (isProcessing) {
      console.warn('[SoloGamemaster] handlePlayerInput skipped — already processing');
      return;
    }

    set({ isProcessing: true, error: null });

    try {
      const result = await trpc.solo.handleAction.mutate({
        campaignId,
        playerInput: text,
      });
      const commands = (result.commands || []) as GamemasterCommand[];

      // Check for choice buttons to embed in the narration message
      const choiceCmd = commands.find((c): c is PresentChoicesCommand => c.type === 'present_choices');

      // Add AI DM narration to chat (with choice buttons if present)
      if (result.narration || choiceCmd) {
        useChatStore.getState().addMessage({
          sender: 'Dungeon Master',
          message: result.narration || '',
          type: 'ai_dm',
          choiceButtons: choiceCmd?.choices,
        });
      }

      // Execute non-choice commands
      const otherCommands = commands.filter((c) => c.type !== 'present_choices');
      if (otherCommands.length > 0) {
        await executeCommands(otherCommands, campaignId);
      }

      // Ensure all PCs have tokens after commands execute (map may have changed)
      const allCharsAfter = useCharacterStore.getState().characters;
      const pcCharsAfter = Array.from(allCharsAfter.values()).filter(
        (c) => c.campaignId === campaignId && !c.isNpc,
      );
      if (pcCharsAfter.length > 0) ensurePartyTokens(pcCharsAfter);

      // Track pending roll
      const pendingRoll = result.pendingRoll?.pendingRoll || null;
      if (pendingRoll) {
        addRollRequestToChat(pendingRoll);
      }

      set({ isProcessing: false, pendingRoll });
    } catch (err) {
      console.error('[SoloGamemaster] Handle action failed:', err);
      set({ error: (err as Error).message, isProcessing: false });
    }
  },

  handleRollResult: async (notation, total, natural, label) => {
    const { campaignId, isSoloMode, isProcessing } = get();
    if (!campaignId || !isSoloMode) return;
    if (isProcessing) {
      console.warn('[SoloGamemaster] handleRollResult skipped — already processing');
      return;
    }

    set({ isProcessing: true, error: null });

    try {
      const result = await trpc.solo.adjudicateRoll.mutate({
        campaignId,
        notation,
        total,
        natural,
        label,
      });

      if (result.narration) {
        useChatStore.getState().addMessage({
          sender: 'Dungeon Master',
          message: result.narration,
          type: 'ai_dm',
        });
      }

      if (result.commands.length > 0) {
        await executeCommands(result.commands as GamemasterCommand[], campaignId);
      }

      const pendingRoll = result.pendingRoll?.pendingRoll || null;
      if (pendingRoll) {
        addRollRequestToChat(pendingRoll);
      }

      set({ isProcessing: false, pendingRoll });
    } catch (err) {
      console.error('[SoloGamemaster] Adjudicate roll failed:', err);
      set({ error: (err as Error).message, isProcessing: false });
    }
  },

  endCombatTurn: async () => {
    const { campaignId, isSoloMode, isProcessing } = get();
    if (!campaignId || !isSoloMode) return;
    if (isProcessing) {
      console.warn('[SoloGamemaster] endCombatTurn skipped — already processing');
      return;
    }

    set({ isProcessing: true, error: null });

    try {
      const result = await trpc.solo.endCombatTurn.mutate({ campaignId });

      if (result.narration) {
        useChatStore.getState().addMessage({
          sender: 'Dungeon Master',
          message: result.narration,
          type: 'ai_dm',
        });
      }

      if (result.commands.length > 0) {
        await executeCommands(result.commands as GamemasterCommand[], campaignId);
      }

      const pendingRoll = result.pendingRoll?.pendingRoll || null;
      if (pendingRoll) {
        addRollRequestToChat(pendingRoll);
      }

      set({ isProcessing: false, pendingRoll });
    } catch (err) {
      console.error('[SoloGamemaster] End combat turn failed:', err);
      set({ error: (err as Error).message, isProcessing: false });
    }
  },

  clearPendingRoll: () => set({ pendingRoll: null }),
}));

/**
 * Add a roll request card to chat
 */
function addRollRequestToChat(roll: PendingRollRequest): void {
  const skillOrAbility = roll.skill || roll.ability || '';
  const typeLabel = roll.rollType === 'saving_throw' ? 'Saving Throw' :
    roll.rollType === 'skill_check' ? 'Skill Check' :
    roll.rollType === 'ability_check' ? 'Ability Check' :
    roll.rollType === 'attack' ? 'Attack Roll' : 'Roll';

  const msg: Omit<ChatMessage, 'id' | 'timestamp'> = {
    sender: 'Dungeon Master',
    message: `${typeLabel}: ${skillOrAbility} — ${roll.reason}`,
    type: 'roll_request',
    rollRequestData: {
      id: roll.id,
      rollType: roll.rollType,
      ability: roll.ability,
      skill: roll.skill,
      dc: roll.dc,
      reason: roll.reason,
      hideDc: roll.hideDc,
      resolved: false,
    },
  };
  useChatStore.getState().addMessage(msg);
}
